import { apiClient } from './client';

// ============================================================================
// Types
// ============================================================================

export interface PurchaseOrder {
  id: string;
  poNumber: string;
  supplierId: string;
  supplier?: { id: string; name: string };
  orderDate: string;
  expectedDate: string;
  status: 'DRAFT' | 'RELEASED' | 'PARTIAL' | 'COMPLETED' | 'CANCELLED';
  totalAmount?: number;
  notes?: string;
  releasedAt?: string;
  lines?: PurchaseOrderLine[];
  goodsReceipts?: GoodsReceipt[];
}

export interface PurchaseOrderLine {
  id: string;
  productId: string;
  product?: { id: string; sku: string; name: string };
  quantity: number;
  unitPrice: number;
  receivedQuantity: number;
}

export interface GoodsReceipt {
  id: string;
  grNumber: string;
  purchaseOrderId: string;
  receiptDate: string;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED';
  notes?: string;
  lines?: GoodsReceiptLine[];
}

export interface GoodsReceiptLine {
  id: string;
  purchaseOrderLineId: string;
  quantity: number;
  lotNumber?: string;
}

export interface WorkOrder {
  id: string;
  woNumber: string;
  productId: string;
  product?: { id: string; sku: string; name: string };
  quantity: number;
  completedQuantity: number;
  scrapQuantity: number;
  scheduledStart: string;
  scheduledEnd: string;
  actualStart?: string;
  actualEnd?: string;
  status: 'PLANNED' | 'RELEASED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  priority: number;
  workCenterId?: string;
  workCenter?: { id: string; name: string };
  bomId?: string;
  routingId?: string;
  notes?: string;
  operations?: WorkOrderOperation[];
  materialIssues?: MaterialIssue[];
  completions?: ProductionCompletion[];
}

export interface WorkOrderOperation {
  id: string;
  sequence: number;
  operationName: string;
  workCenterId?: string;
  workCenter?: { id: string; name: string };
  plannedSetupTime: number;
  plannedRunTime: number;
  actualSetupTime?: number;
  actualRunTime?: number;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  actualStart?: string;
  actualEnd?: string;
  notes?: string;
  laborEntries?: LaborEntry[];
}

export interface MaterialIssue {
  id: string;
  workOrderId: string;
  productId: string;
  product?: { id: string; sku: string; name: string };
  quantity: number;
  issueDate: string;
  lotNumber?: string;
  notes?: string;
}

export interface ProductionCompletion {
  id: string;
  workOrderId: string;
  operationId?: string;
  quantity: number;
  scrapQuantity: number;
  completionDate: string;
  lotNumber?: string;
  notes?: string;
}

export interface LaborEntry {
  id: string;
  operationId: string;
  laborType: 'SETUP' | 'RUN' | 'TEARDOWN' | 'REWORK';
  startTime: string;
  endTime: string;
  hours: number;
  employeeId?: string;
  notes?: string;
}

// ============================================================================
// Purchase Order Service
// ============================================================================

export const purchaseOrderService = {
  async getAll(params?: {
    status?: string;
    supplierId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    filters?: string;
  }): Promise<
    { items: PurchaseOrder[]; total: number; page: number; pageSize: number; totalPages: number } | PurchaseOrder[]
  > {
    const response = await apiClient.get('/manufacturing/purchase-orders', { params });
    return response.data;
  },

  async getById(id: string): Promise<PurchaseOrder> {
    const response = await apiClient.get(`/manufacturing/purchase-orders/${id}`);
    return response.data;
  },

  async create(dto: {
    supplierId: string;
    expectedDate: string;
    lines: Array<{
      productId: string;
      quantity: number;
      unitPrice: number;
    }>;
    notes?: string;
  }): Promise<PurchaseOrder> {
    const response = await apiClient.post('/manufacturing/purchase-orders', dto);
    return response.data;
  },

  async release(id: string): Promise<PurchaseOrder> {
    const response = await apiClient.post(`/manufacturing/purchase-orders/${id}/release`);
    return response.data;
  },

  async cancel(id: string, reason?: string): Promise<PurchaseOrder> {
    const response = await apiClient.post(`/manufacturing/purchase-orders/${id}/cancel`, { reason });
    return response.data;
  },
};

// ============================================================================
// Goods Receipt Service
// ============================================================================

export const goodsReceiptService = {
  async create(dto: {
    purchaseOrderId: string;
    lines: Array<{
      purchaseOrderLineId: string;
      quantity: number;
      lotNumber?: string;
    }>;
    notes?: string;
  }): Promise<GoodsReceipt> {
    const response = await apiClient.post('/manufacturing/goods-receipts', dto);
    return response.data;
  },

  async confirm(id: string): Promise<GoodsReceipt> {
    const response = await apiClient.post(`/manufacturing/goods-receipts/${id}/confirm`);
    return response.data;
  },
};

// ============================================================================
// Work Order Service
// ============================================================================

export const workOrderService = {
  async getAll(params?: {
    status?: string;
    workCenterId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    filters?: string;
  }): Promise<{ items: WorkOrder[]; total: number; page: number; pageSize: number; totalPages: number } | WorkOrder[]> {
    const response = await apiClient.get('/manufacturing/work-orders', { params });
    return response.data;
  },

  async getById(id: string): Promise<WorkOrder> {
    const response = await apiClient.get(`/manufacturing/work-orders/${id}`);
    return response.data;
  },

  async create(dto: {
    productId: string;
    quantity: number;
    scheduledStart: string;
    scheduledEnd: string;
    workCenterId?: string;
    bomId?: string;
    routingId?: string;
    priority?: number;
    notes?: string;
  }): Promise<WorkOrder> {
    const response = await apiClient.post('/manufacturing/work-orders', dto);
    return response.data;
  },

  async release(id: string): Promise<WorkOrder> {
    const response = await apiClient.post(`/manufacturing/work-orders/${id}/release`);
    return response.data;
  },

  async start(id: string): Promise<WorkOrder> {
    const response = await apiClient.post(`/manufacturing/work-orders/${id}/start`);
    return response.data;
  },

  async complete(id: string): Promise<WorkOrder> {
    const response = await apiClient.post(`/manufacturing/work-orders/${id}/complete`);
    return response.data;
  },

  async cancel(id: string, reason?: string): Promise<WorkOrder> {
    const response = await apiClient.post(`/manufacturing/work-orders/${id}/cancel`, { reason });
    return response.data;
  },
};

// ============================================================================
// Production Completion Service
// ============================================================================

export const productionCompletionService = {
  async report(dto: {
    workOrderId: string;
    quantity: number;
    scrapQuantity?: number;
    operationId?: string;
    lotNumber?: string;
    notes?: string;
  }): Promise<ProductionCompletion> {
    const response = await apiClient.post('/manufacturing/production-completions', dto);
    return response.data;
  },
};

// ============================================================================
// Labor Entry Service
// ============================================================================

export const laborEntryService = {
  async record(dto: {
    operationId: string;
    laborType: 'SETUP' | 'RUN' | 'TEARDOWN' | 'REWORK';
    startTime: string;
    endTime: string;
    employeeId?: string;
    notes?: string;
  }): Promise<LaborEntry> {
    const response = await apiClient.post('/manufacturing/labor-entries', dto);
    return response.data;
  },
};

// Export all services
export default {
  purchaseOrder: purchaseOrderService,
  goodsReceipt: goodsReceiptService,
  workOrder: workOrderService,
  productionCompletion: productionCompletionService,
  laborEntry: laborEntryService,
};
